<template>
  <div class="custom-product">
    <!-- Product image -->
    <div class="product-img-wrap">
      <img
        v-if="product.featuredImage"
        :src="product.featuredImage.url"
        :alt="product.featuredImage.altText || product.title"
        class="product-img"
      />
    </div>

    <!-- Product title -->
    <h3 class="product-title">{{ product.title }}</h3>

    <!-- Price -->
    <p class="product-price">${{ formattedPrice }}</p>
    <p v-if="limitOne" class="limit-note">Limit one per order</p>

    <!-- Variant selector (only if more than one variant) -->
    <div v-if="product.variants.nodes.length > 1" class="variant-selector">
      <label :for="`variant-${product.id}`" class="variant-label">
        Select option:
      </label>
      <select
        :id="`variant-${product.id}`"
        v-model="selectedVariantId"
        class="variant-select"
      >
        <option
          v-for="variant in product.variants.nodes"
          :key="variant.id"
          :value="variant.id"
          :disabled="!variant.availableForSale"
        >
          {{ variant.title }}
          <span v-if="!variant.availableForSale"> (Sold out)</span>
        </option>
      </select>
    </div>

    <!-- Add to cart button -->
    <button
      class="add-to-cart-btn"
      :disabled="!selectedVariant.availableForSale || adding || alreadyInCart"
      @click="onAddToCart"
    >
      {{
        adding ? "Adding..." : alreadyInCart ? "Already in cart" : "Add to cart"
      }}
    </button>
  </div>
</template>

<script>
export default {
  name: "CustomBuyButton",
  props: {
    product: {
      type: Object,
      required: true,
    },
    noPlate: {
      type: Boolean,
      default: false,
    },
    extraPlates: {
      type: Number,
      default: 0,
    },
    // Only one of this item may be in the cart, at quantity 1 (e.g. annual pass).
    limitOne: {
      type: Boolean,
      default: false,
    },
    // Set by the parent when a limit-one item is already in the cart.
    inCart: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedVariantId:
        this.product.variants.nodes.find((v) => v.availableForSale)?.id ||
        this.product.variants.nodes[0].id,
      adding: false,
    };
  },
  computed: {
    selectedVariant() {
      return (
        this.product.variants.nodes.find(
          (v) => v.id === this.selectedVariantId
        ) || this.product.variants.nodes[0]
      );
    },
    formattedPrice() {
      return parseFloat(this.selectedVariant.price.amount).toFixed(2);
    },
    alreadyInCart() {
      return this.limitOne && this.inCart;
    },
  },
  methods: {
    async onAddToCart() {
      const NO_PLATE_KEYWORDS = [
        "pedestrian",
        "cyclist",
        "commercial passenger",
        "walk-in",
        "bicycle",
        "hikers",
        "bikers",
      ];
      const productTitle = this.product.title.toLowerCase();
      const variantTitle = this.selectedVariant.title.toLowerCase();
      const requiresPlate =
        !this.noPlate &&
        !NO_PLATE_KEYWORDS.some(
          (kw) => productTitle.includes(kw) || variantTitle.includes(kw)
        );

      const attributes = [
        { key: "_requiresPlate", value: requiresPlate ? "true" : "false" },
        { key: "_extraPlates", value: String(this.extraPlates) },
      ];
      if (this.limitOne) {
        attributes.push({ key: "_limitOne", value: "true" });
      }

      this.adding = true;
      try {
        this.$emit("add-to-cart", {
          merchandiseId: this.selectedVariantId,
          quantity: 1,
          attributes,
        });
      } finally {
        setTimeout(() => {
          this.adding = false;
        }, 600);
      }
    },
  },
};
</script>

<style scoped>
.custom-product {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #fff;
  border-radius: 8px;
}

.product-img-wrap {
  position: relative;
  width: 100%;
  padding-top: 75%;
  margin-bottom: 12px;
  overflow: hidden;
  border-radius: 4px;
  background: #f5f5f5;
}

.product-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-title {
  font-family: "Roboto", sans-serif;
  font-size: 18px;
  font-weight: 400;
  margin: 0 0 8px 0;
  color: #4c4c4c;
  text-align: center;
}

.variant-selector {
  margin: 4px 0;
  width: 100%;
}

.variant-label {
  display: block;
  color: #515151;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-bottom: 4px;
}

.variant-select {
  width: 325px;
  max-width: 100%;
  height: 40px;
  box-sizing: border-box;
  padding: 0 32px 0 12px;
  border: 1px solid #808080;
  border-radius: 6px;
  color: #4c4c4c;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  background: #fff
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%234c4c4c' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
}

.variant-select:focus {
  outline: 2px solid #727a35;
  outline-offset: 1px;
}

.product-price {
  font-family: "Roboto", sans-serif;
  font-weight: bold;
  font-size: 18px;
  margin: 4px 0;
  color: #4c4c4c;
  text-align: center;
}

.add-to-cart-btn {
  background-color: #727a35;
  color: #fff;
  border: none;
  border-radius: 40px;
  padding: 12px 24px;
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: auto;
  transition: background-color 0.15s ease;
  margin-top: 15px;
}

.add-to-cart-btn:hover:not(:disabled),
.add-to-cart-btn:focus:not(:disabled) {
  background-color: #87913e;
}

.add-to-cart-btn:focus {
  outline: 2px solid #fff;
  outline-offset: -4px;
  box-shadow: 0 0 0 4px #727a35;
}

.add-to-cart-btn:disabled {
  background-color: #9f9f9f;
  cursor: not-allowed;
}

.limit-note {
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: #4c4c4c;
  margin: 0 0 4px;
  text-align: center;
}
</style>
